import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import Project from '../../../models/Project';
import { getProjectByESGBarcharts } from '../../../redux/actions/indicatorProjects';
import { getDynamicTableFormat } from '../../../services/util';
import { PortfolioSimpleIndicatorTable } from '../PortfolioSimpleIndicatorTable/PortfolioSimpleIndicatorTable';
import { ProjectCounter } from '../ProjectCounter/ProjectCounter';
import './WorkWithFilteredChart.scss';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';

type Props = {
  currentIndicator: any;
  conceptFilter: any;
  sectorFilters?: any;
  regionFilters?: any;
  loaderSelects?: boolean;
};

enum indicatorType {
  ESPR = 'ESG_SAFEGUARD_PERFORMANCE_RATING',
  EEASRR = 'ESG_ENVIRONMENTAL_AND_SOCIAL_RISK_RATING',
}

export const WorkWithFilterdChart = ({
  currentIndicator: { id: indicatorId, indicatorKey },
  conceptFilter,
  sectorFilters,
  regionFilters,
  loaderSelects,
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const indicatorProjects = useSelector<any, Project[]>(state => state.indicatorProjects);

  // Filter

  const filterIndicatorsBar = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const loadingRedux = useSelector<any, boolean>(state => state.dataTableInfo.loading);
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = useState(false);
  const [totalProjectsFilter, setTotalProjectsFilter] = useState(false);
  const [flag, setFlag] = useState<boolean>(false);

  const projectTypeFilterChange = {
    ...filterIndicatorsBar,
    common: { ...filterIndicatorsBar.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async indicatorKey => {
    dispatch(setLoading(true));
    setIsLoading(true);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicatorsBar.common.projectTypeFilters,
        indicatorKey,
      );
      setTotalProjectsFilter(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan], indicatorKey) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));

    if (indicatorKey === indicatorType.EEASRR) {
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(data => data.indicatorKey === 'ESG_ENVIRONMENTAL_AND_SOCIAL_RISK_RATING'),
        ),
      );
    } else if (indicatorKey === indicatorType.ESPR) {
      dispatch(
        setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'ESG_SAFEGUARD_PERFORMANCE_RATING')),
      );
    }

    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (conceptFilter.label !== '') {
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, sectorFilters, regionFilters },
        projectTypeFilterChange,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptFilter, sectorFilters, regionFilters, filterIndicatorsBar.common]);

  useEffect(() => {
    if (
      filterIndicatorsBar.common.coReponsibleDivisions.length > 0 ||
      filterIndicatorsBar.common.countryId.length > 0 ||
      filterIndicatorsBar.common.divisionId.length > 0 ||
      filterIndicatorsBar.common.interestIssues.length > 0 ||
      filterIndicatorsBar.common.participantDivisions.length > 0 ||
      filterIndicatorsBar.common.regionId.length > 0 ||
      filterIndicatorsBar.common.sectors.length > 0 ||
      filterIndicatorsBar.common.teamLeaders.length > 0
    )
      handleCommonFilterChange(indicatorKey);
    setFlag(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicatorsBar.common.coReponsibleDivisions.length === 0 &&
      filterIndicatorsBar.common.countryId.length === 0 &&
      filterIndicatorsBar.common.divisionId.length === 0 &&
      filterIndicatorsBar.common.interestIssues.length === 0 &&
      filterIndicatorsBar.common.participantDivisions.length === 0 &&
      filterIndicatorsBar.common.regionId.length === 0 &&
      filterIndicatorsBar.common.sectors.length === 0 &&
      filterIndicatorsBar.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange(indicatorKey);
      setFlag(true);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  return (
    <>
      {!loadingRedux && !loaderSelects ? (
        <div className="work-with-filtered-chart-container">
          <div className='filtered-label'>
            <div className="title">{renderTitle(indicatorKey, t)}</div>
            <div className={`esg-text ${conceptFilter.label}`}>
              {conceptFilter.label === 'not_assigned' ? t('not_assigned_exp_not_set') : t(conceptFilter.label)}
            </div>
          </div>
          <div className="table-container">
            {renderTableByIndicatorKey(indicatorKey, indicatorProjects)}
            <ProjectCounter arrayToCount={indicatorProjects} countProjects={true} indicatorKey={indicatorKey} />
          </div>
        </div>
      ) : (
        <ProjectsListSkeleton />
      )}
    </>
  );
};

const renderTitle = (indicatorKey, t) => {
  switch (indicatorKey) {
    case IndicatorKeyEnum.ESG_SAFEGUARD_PERFORMANCE_RATING:
      return t('performance');
    case IndicatorKeyEnum.ESG_ENVIRONMENTAL_AND_SOCIAL_RISK_RATING:
      return t('risk');

    default:
      return '';
  }
};

const renderTableByIndicatorKey = (indicatorKey, indicatorProjects) => {
  return (
    <PortfolioSimpleIndicatorTable
      projects={indicatorProjects}
      tableInfo={getDynamicTableFormat(indicatorKey)}
      indicatorKey={indicatorKey}
    />
  );
};

export const getFilteredProjects = (d, indicatorInfo, projectTypeFilterChange?) => {
  let returnedDispatch;

  if (
    indicatorInfo.indicatorKey === IndicatorKeyEnum.ESG_SAFEGUARD_PERFORMANCE_RATING ||
    indicatorInfo.indicatorKey === IndicatorKeyEnum.ESG_ENVIRONMENTAL_AND_SOCIAL_RISK_RATING
  ) {
    returnedDispatch = d(
      getProjectByESGBarcharts(
        indicatorInfo.indicatorKey === IndicatorKeyEnum.ESG_SAFEGUARD_PERFORMANCE_RATING ? 'EsgSafeguard' : 'EsgRisk',
        indicatorInfo.conceptFilter.label,
        indicatorInfo.conceptFilter.totalProjects,
        indicatorInfo.indicatorId,
        indicatorInfo.sectorFilters,
        indicatorInfo.regionFilters,
        projectTypeFilterChange,
      ),
    );
  }

  return returnedDispatch;
};
